// src/components/admin/admin/PortfolioManagement.jsx
import React, { useState, useEffect } from "react";
import api from "../../../services/httpService";
import "./Portfolio.css";

const PortfolioManagement = () => {
  // Hero Section State
  const [heroData, setHeroData] = useState(null);
  const [isHeroLoading, setIsHeroLoading] = useState(true);
  const [isHeroSaving, setIsHeroSaving] = useState(false);
  const [heroMessage, setHeroMessage] = useState("");
  const [isHeroEditing, setIsHeroEditing] = useState(false);
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [heroImagePreview, setHeroImagePreview] = useState(null);

  // Portfolio Items State
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [isPortfolioLoading, setIsPortfolioLoading] = useState(true);
  const [portfolioMessage, setPortfolioMessage] = useState("");

  // Form State
  const [activeForm, setActiveForm] = useState(null); // 'add' | 'edit' | null
  const [formData, setFormData] = useState({
    id: "",
    title: "",
    description: "",
    imageKey: "",
    type: "portfolio",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [serviceImageFile, setServiceImageFile] = useState(null);
  const [serviceImagePreview, setServiceImagePreview] = useState(null);

  useEffect(() => {
    fetchHeroData();
    fetchPortfolioItems();
  }, []);

  // ==================== HERO FUNCTIONS ====================
  const fetchHeroData = async () => {
    setIsHeroLoading(true);
    try {
      const res = await api.get("/sentinix/portfolios");
      if (res.data.success) {
        const raw = res.data.data;
        const allItems = Array.isArray(raw) ? raw : raw ? [raw] : [];
        const hero = allItems.find((item) => item.type === "hero" && !item.isDeleted);
        setHeroData(hero || null);
      }
    } catch (error) {
      console.error("Error fetching hero data:", error);
      setHeroData(null);
    } finally {
      setIsHeroLoading(false);
    }
  };

  const handleHeroImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setHeroImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setHeroImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSaveHero = async () => {
    if (!heroData?.title?.trim() || !heroData?.description?.trim()) {
      setHeroMessage("Title and Description are required");
      setTimeout(() => setHeroMessage(""), 3000);
      return;
    }

    setIsHeroSaving(true);
    setHeroMessage("");

    try {

      const formDataPayload = new FormData();
      formDataPayload.append("type", "hero");
      formDataPayload.append("title", heroData.title);
      formDataPayload.append("description", heroData.description);

      if (heroImageFile) {
        formDataPayload.append("imageKey", heroImageFile);
      } else if (heroData.imageKey) {
        formDataPayload.append("imageKey", heroData.imageKey);
      }

      let res;
      if (heroData?.id) {
        res = await api.put(
          `/sentinix/portfolios/${heroData.id}`,
          formDataPayload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        res = await api.post(
          "/sentinix/portfolios",
          formDataPayload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      if (res.data.success) {
        setHeroMessage("Hero section saved successfully!");
        setIsHeroEditing(false);
        setHeroImageFile(null);
        setHeroImagePreview(null);
        fetchHeroData();
      } else {
        setHeroMessage(res.data.message || "Failed to save hero section.");
      }
    } catch (error) {
      console.error("Error saving hero data:", error);
      setHeroMessage(error.response?.data?.message || "Error saving hero data");
    } finally {
      setIsHeroSaving(false);
      setTimeout(() => setHeroMessage(""), 3000);
    }
  };

  // ==================== PORTFOLIO FUNCTIONS ====================
  const fetchPortfolioItems = async () => {
    setIsPortfolioLoading(true);
    try {
      const res = await api.get("/sentinix/portfolios");
      if (res.data.success) {
        const raw = res.data.data;
        const allItems = Array.isArray(raw) ? raw : raw ? [raw] : [];
        // Filter only portfolio type items
        const items = allItems.filter(
          (item) => item.type === "portfolio" && !item.isDeleted
        );
        setPortfolioItems(items);
      }
    } catch (error) {
      console.error("Error fetching portfolio items:", error);
    } finally {
      setIsPortfolioLoading(false);
    }
  };

  const handleServiceImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setServiceImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setServiceImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // ==================== FORM HANDLERS ====================
  const openAddForm = () => {
    setActiveForm("add");
    setFormData({ id: "", title: "", description: "", imageKey: "", type: "portfolio" });
    setServiceImageFile(null);
    setServiceImagePreview(null);
    setPortfolioMessage("");
  };

  const openEditForm = (item) => {
    setActiveForm("edit");
    setFormData({
      id: item.id,
      title: item.title,
      description: item.description,
      imageKey: item.imageKey || "",
      type: item.type || "portfolio",
    });
    setServiceImageFile(null);
    setServiceImagePreview(null);
    setPortfolioMessage("");
  };

  const closeForm = () => {
    setActiveForm(null);
    setFormData({ id: "", title: "", description: "", imageKey: "", type: "portfolio" });
    setServiceImageFile(null);
    setServiceImagePreview(null);
    setPortfolioMessage("");
  };

  const handleSavePortfolio = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      setPortfolioMessage("Title and Description are required");
      setTimeout(() => setPortfolioMessage(""), 3000);
      return;
    }

    setIsSaving(true);
    setPortfolioMessage("");

    try {
      // ✅ Always use FormData
      const formDataPayload = new FormData();
      formDataPayload.append("type", "portfolio");
      formDataPayload.append("title", formData.title);
      formDataPayload.append("description", formData.description);

      if (serviceImageFile) {
        // ✅ New image selected — send file
        formDataPayload.append("imageKey", serviceImageFile);
      } else if (formData.imageKey) {
        // ✅ No new image — keep existing Cloudinary URL
        formDataPayload.append("imageKey", formData.imageKey);
      }

      let res;
      if (activeForm === "edit" && formData.id) {
        res = await api.put(
          `/sentinix/portfolios/${formData.id}`,
          formDataPayload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        res = await api.post(
          "/sentinix/portfolios",
          formDataPayload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      if (res.data.success) {
        setPortfolioMessage(
          activeForm === "edit"
            ? "Portfolio updated successfully!"
            : "Portfolio created successfully!"
        );
        fetchPortfolioItems();
        setTimeout(() => closeForm(), 1500);
      } else {
        setPortfolioMessage(res.data.message || "Failed to save.");
      }
    } catch (error) {
      console.error("Error saving portfolio:", error);
      setPortfolioMessage(error.response?.data?.message || "Error saving data");
    } finally {
      setIsSaving(false);
      setTimeout(() => setPortfolioMessage(""), 3000);
    }
  };

  const handleDeletePortfolio = async (id) => {
    if (!window.confirm("Are you sure you want to delete this portfolio item?")) return;

    try {
      const res = await api.delete(`/sentinix/portfolios/${id}`);
      if (res.data.success) {
        setPortfolioMessage("Portfolio item deleted successfully");
        fetchPortfolioItems();
      } else {
        setPortfolioMessage(res.data.message || "Failed to delete");
      }
    } catch (error) {
      console.error("Error deleting portfolio:", error);
      setPortfolioMessage(error.response?.data?.message || "Error deleting item");
    } finally {
      setTimeout(() => setPortfolioMessage(""), 3000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="portfolio-mgmt-container">
      {/* Page Header */}
      <div className="portfolio-page-header">
        <h1>Portfolio Management</h1>
        <p>Manage portfolio hero section and individual portfolio items</p>
      </div>

      {/* ==================== HERO SECTION ==================== */}
      <div className="portfolio-card">
        <div className="portfolio-card-header">
          <div>
            <h2>Portfolio Hero Section</h2>
            <p>Manage your portfolio page hero section</p>
          </div>
          <button
            onClick={() => {
              setIsHeroEditing(!isHeroEditing);
              if (isHeroEditing) {
                setHeroImageFile(null);
                setHeroImagePreview(null);
              }
            }}
            className={`btn-toggle-edit ${isHeroEditing ? "cancel" : "edit"}`}
          >
            {isHeroEditing ? "✕ Cancel" : "✏️ Edit"}
          </button>
        </div>

        {isHeroLoading ? (
          <div className="loading-box">
            <div className="spinner"></div>
            <p>Loading hero data...</p>
          </div>
        ) : (
          <div className="portfolio-card-body">
            {heroMessage && (
              <div className={`form-message ${heroMessage.includes("success") ? "success" : "error"}`}>
                {heroMessage}
              </div>
            )}

            {/* Title */}
            <div className="form-group-portfolio">
              <label>Title <span className="required">*</span></label>
              <input
                type="text"
                value={heroData?.title || ""}
                onChange={(e) =>
                  setHeroData((prev) =>
                    prev
                      ? { ...prev, title: e.target.value }
                      : { type: "hero", title: e.target.value, description: "", imageKey: "" }
                  )
                }
                readOnly={!isHeroEditing && !!heroData}
                placeholder="Enter portfolio page title"
                className={`form-input-portfolio ${!isHeroEditing && heroData ? "readonly" : ""}`}
              />
            </div>

            {/* Image */}
            <div className="form-group-portfolio">
              <label>Image</label>
              {(heroImagePreview || heroData?.imageKey) && (
                <div className="image-preview-portfolio">
                  <img
                    src={heroImagePreview || heroData?.imageKey}
                    alt="Hero"
                  />
                </div>
              )}
              {isHeroEditing && (
                <div className="image-upload-portfolio">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleHeroImageSelect}
                    className="file-input-portfolio"
                  />
                  {heroImageFile && (
                    <p className="image-note">✅ {heroImageFile.name} — will upload on Save</p>
                  )}
                  {!heroImageFile && heroData?.imageKey && (
                    <p className="image-note">ℹ️ No new image selected — existing image will be kept</p>
                  )}
                </div>
              )}
            </div>

            {/* Description */}
            <div className="form-group-portfolio">
              <label>Description <span className="required">*</span></label>
              <textarea
                value={heroData?.description || ""}
                onChange={(e) =>
                  setHeroData((prev) =>
                    prev
                      ? { ...prev, description: e.target.value }
                      : { type: "hero", title: "", description: e.target.value, imageKey: "" }
                  )
                }
                readOnly={!isHeroEditing && !!heroData}
                rows={4}
                placeholder="Enter portfolio page description"
                className={`form-textarea-portfolio ${!isHeroEditing && heroData ? "readonly" : ""}`}
              />
            </div>

            {/* Save Button */}
            {isHeroEditing && (
              <div className="form-save-footer">
                <button
                  onClick={handleSaveHero}
                  disabled={isHeroSaving}
                  className="btn-save-portfolio"
                >
                  {isHeroSaving ? (
                    <><span className="spinner-small"></span> Saving...</>
                  ) : (
                    <>{heroData ? "💾 Update Hero Section" : "💾 Create Hero Section"}</>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ==================== PORTFOLIO ITEMS SECTION ==================== */}
      <div className="portfolio-card">
        <div className="portfolio-card-header">
          <div>
            <h2>Portfolio Items</h2>
            <p>Manage your individual portfolio items</p>
          </div>
          <button onClick={openAddForm} className="btn-add-portfolio">
            + Add New Portfolio Item
          </button>
        </div>

        <div className="portfolio-card-body">
          {portfolioMessage && (
            <div className={`form-message ${portfolioMessage.includes("success") ? "success" : "error"}`}>
              {portfolioMessage}
            </div>
          )}

          {/* Add / Edit Form Modal */}
          {activeForm && (
            <div className="modal-overlay" onClick={closeForm}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <div>
                    <h3>{activeForm === "add" ? "Add New Portfolio Item" : "Edit Portfolio Item"}</h3>
                  </div>
                  <button className="modal-close" onClick={closeForm}>✕</button>
                </div>

                <div className="modal-body">
                  {portfolioMessage && (
                    <div className={`form-message ${portfolioMessage.includes("success") ? "success" : "error"}`}>
                      {portfolioMessage}
                    </div>
                  )}

                  {/* Title */}
                  <div className="form-group-portfolio">
                    <label>Title <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Enter portfolio title"
                      className="form-input-portfolio"
                    />
                  </div>

                  {/* Image */}
                  <div className="form-group-portfolio">
                    <label>Image</label>
                    {(serviceImagePreview || formData.imageKey) && (
                      <div className="image-preview-portfolio">
                        <img
                          src={serviceImagePreview || formData.imageKey}
                          alt="Portfolio"
                        />
                      </div>
                    )}
                    <div className="image-upload-portfolio">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleServiceImageSelect}
                        className="file-input-portfolio"
                      />
                      {serviceImageFile && (
                        <p className="image-note">✅ {serviceImageFile.name} — will upload on Save</p>
                      )}
                      {!serviceImageFile && formData.imageKey && (
                        <p className="image-note">ℹ️ No new image selected — existing image will be kept</p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="form-group-portfolio">
                    <label>Description <span className="required">*</span></label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={6}
                      placeholder="Enter portfolio description..."
                      className="form-textarea-portfolio"
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button onClick={closeForm} className="btn-close-modal">Cancel</button>
                  <button
                    onClick={handleSavePortfolio}
                    disabled={isSaving}
                    className="btn-save-portfolio"
                  >
                    {isSaving ? (
                      <><span className="spinner-small"></span> Saving...</>
                    ) : (
                      <>{activeForm === "edit" ? "💾 Update Portfolio" : "💾 Save Portfolio"}</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Portfolio Grid */}
          {isPortfolioLoading ? (
            <div className="loading-box">
              <div className="spinner"></div>
              <p>Loading portfolio items...</p>
            </div>
          ) : portfolioItems.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">🖼️</span>
              <p>No portfolio items yet</p>
              <button onClick={openAddForm} className="btn-add-portfolio">
                + Add Your First Portfolio Item
              </button>
            </div>
          ) : (
            <div className="portfolio-grid">
              {portfolioItems.map((item) => (
                <div key={item.id} className="portfolio-item-card">
                  <div className="portfolio-item-image">
                    {item.imageKey ? (
                      <img src={item.imageKey} alt={item.title} />
                    ) : (
                      <div className="no-image-placeholder">
                        <span>No Image</span>
                      </div>
                    )}
                  </div>

                  <div className="portfolio-item-info">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <span className="portfolio-item-date">{formatDate(item.createdAt)}</span>
                  </div>

                  <div className="portfolio-item-actions">
                    <button
                      onClick={() => openEditForm(item)}
                      className="btn-edit-item"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeletePortfolio(item.id)}
                      className="btn-delete-item"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioManagement;